import { Component, EventEmitter, inject, input, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { BaseComponent } from '@shared-components/base-component';
import { ComponentHeaderComponent } from '@shared-components/component-header/component-header.component';
import { CreateAccountComponent } from '@shared-components/create-account/create-account.component';
import { ModalComponent } from '@shared-components/modal/modal.component';
import { AppHelper } from '@shared-helpers/appHelper';
import { FormHelper } from '@shared-helpers/formHelper';
import { ProductHelper } from '@shared-helpers/productHelper';
import { ExpertModel } from '@shared-model/expert/ExpertModel';
import { ProductModel } from '@shared-model/products/ProductModel';
import { ServiceOperationOutcome } from '@shared-results/ServiceOperationOutcome';
import { ValidationOutcome } from '@shared-results/ValidationOutcome';
import { LoginService } from '@shared-services/login.service';

@Component({
  selector: 'fourd-login',
  imports: [ReactiveFormsModule, ModalComponent, ComponentHeaderComponent, CreateAccountComponent],
  templateUrl: './login.component.html',
  host: {
    '[class]': 'chatClass',
  },
  providers: [...BaseComponent.componentProviders],
})
export class LoginComponent extends BaseComponent implements OnInit {
  private loginService = inject(LoginService);
  protected formHelper = inject(FormHelper);

  readonly expert = input<ExpertModel>(undefined);
  readonly productTypeIds = input<number[]>(undefined);
  @Output() close = new EventEmitter<boolean>();
  chatClass: string = "component-container";
  
  usernameControlName = this.moduleConfig?.FormFieldUsernameName;
  passwordControlName = "Password";

  loginForm: FormGroup;

  submittingData: boolean = false;

  get tokenElement(): HTMLInputElement {
    return document?.querySelector<HTMLInputElement>(`input[name=${AppHelper.requestVerificationTokenKey}]`);
  }

  get token(): string {
    //console.log(`Get token '${this.tokenElement?.value}'`);
    return this.tokenElement?.value;
  }
  set token(value: string) {
    //console.log(`Set token '${value}'`);
    if (value) {
      // Value is non blank, attempt to set
      //console.log(`Set token value not blank`);
      let element = this.tokenElement;
      if (element) {
        // Element is found, set value
        //console.log(`Set token element not blank`);
        element.value = value;
      }
    }
  }

  setSubmittingData(state: boolean): void {
    this.submittingData = state;
    if (state) {
      this.loginForm?.disable();
    }
    else {
      this.loginForm?.enable();
    }
  }

  constructor() {
    super("LoginComponent");
  }
     
  ngOnInit() {
    let usernameControlValidators = [Validators.required];
    if (this.moduleConfig?.FormFieldUsernameCanOnlyBeEmail) {
      usernameControlValidators.push(Validators.email);
    }

    this.loginForm = new FormGroup({
      [this.usernameControlName]: new FormControl('', usernameControlValidators),
      [this.passwordControlName]: new FormControl('', [Validators.required]),
    },
      { updateOn: 'blur' }
    );
    this.formHelper?.initialise(this.loginForm, this.subscriptionTracker);
  }

  loginRequest(event: Event): void {
    this.handleClickEvent(event);

    this.formHelper?.calculateValidationSummary(true);

    if (!this.submittingData && this.loginForm?.valid) {
      this.setSubmittingData(true);
      let errorAction = () => {
        this.formHelper?.addCustomErrorMessages(["An error has occurred."]);
        this.constructModalError(`Sorry, an error has occurred trying to log in. Please refresh the page and try again.`);
      };
      this.loginService?.loginRequest(this.loginForm?.controls[this.usernameControlName]?.value, this.loginForm?.controls[this.passwordControlName]?.value, this.token, {
        next: (result) => {
          this.token = result?.VerificationToken;
          this.setSubmittingData(false);
          if (result) {
            if (result.ServiceOperationOutcome == ServiceOperationOutcome.Success && result.CredentialsValidationOutcome == Number(ValidationOutcome.Valid)) {
              // Ensure member data cached. This is also set by the service, but need to ensure set here before close called
              this.memberService?.cacheMemberData(result.MemberData);
              this.onClose();
            }
            else {
              if (result?.CredentialsValidationOutcome == Number(ValidationOutcome.InvalidValue)) {
                this.formHelper?.addCustomErrorMessages(["Supplied details were incorrect."]);
              }
              else {
                errorAction();
              }
            }
          }
          else {
            errorAction();
          }
        },
        error: (error) => {
          this.setSubmittingData(false);
          errorAction();
        }
      });
    }
  }

  onClose(): void {
    this.close.emit();    
  }

  onKeyUp(event: KeyboardEvent): void {
    if (event?.key == 'Enter') {
      event?.stopPropagation();
      // Ensure any validation is triggered by raising blur event
      (event?.target as HTMLElement)?.blur();
      this.loginRequest(event);
    }
  }

  isProductBundleSession(product: ProductModel): boolean {
    return ProductHelper.isProductTypeBundleSession(product?.ProductTypeId);
  }

  isProductPaygSession(product: ProductModel): boolean {
    return ProductHelper.isProductTypePaygSession(product?.ProductTypeId);
  }

  isProductGeneralTopUp(product: ProductModel): boolean {
    return ProductHelper.isProductTypeGeneralTopUp(product?.ProductTypeId);
  }

  isProductMinutesTopUp(product: ProductModel): boolean {
    return ProductHelper.isProductTypeMinutesTopUp(product?.ProductTypeId);
  }

  showSimplifiedOffer(offerProduct: ProductModel): boolean {
    return ProductHelper.isOfferLoyalty(offerProduct?.OfferId) || ProductHelper.isOfferIntroductory(offerProduct?.OfferId);
  }
}
